import * as THREE from 'three';

/**
 * Ambiente del bosque tenebroso: cielo nocturno con luna y estrellas, niebla, bancos de bruma en el
 * abismo, luciérnagas, la luz del farol del aventurero y sombras de contacto bajo los personajes.
 * Todo se genera por código (texturas pintadas en un canvas). Solo para el navegador.
 */
const FOG = 0x15252b;
const MOON_DIR = new THREE.Vector3(-0.45, 0.42, -0.79).normalize();
const MIST_SIZE = 260;
const FIREFLIES = 70;
// Zonas del bosque: el ambiente cambia cada ZONE_LENGTH metros (con un fundido de ZONE_FADE m).
const ZONE_LENGTH = 1200;
const ZONE_FADE = 150;
const ZONES = [
  { fog: 0x15252b, mid: 0x0c1a26, hemi: 0x9ab8cc }, // noche azul
  { fog: 0x13281c, mid: 0x0a1c12, hemi: 0x9fcf9c }, // bruma verde
  { fog: 0x221b33, mid: 0x150f28, hemi: 0xb7a3d6 }, // crepúsculo violeta
  { fog: 0x27323a, mid: 0x131b22, hemi: 0xbac7d0 }, // niebla gris
].map((z) => ({ fog: new THREE.Color(z.fog), mid: new THREE.Color(z.mid), hemi: new THREE.Color(z.hemi) }));
const _zoneFog = new THREE.Color();
const _zoneMid = new THREE.Color();
const _skyColor = new THREE.Color();
const SKY_TOP = new THREE.Color(0x03070d);
const FIREFLY_BOX = 26; // metros alrededor del jugador

function canvasTexture(size, draw) {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  draw(canvas.getContext('2d'), size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** Punto difuso blanco (partículas, luciérnagas, sombras). */
export function softDot(size = 64) {
  return canvasTexture(size, (g, s) => {
    const grad = g.createRadialGradient(s / 2, s / 2, 0, s / 2, s / 2, s / 2);
    grad.addColorStop(0, 'rgba(255,255,255,1)');
    grad.addColorStop(0.35, 'rgba(255,255,255,0.6)');
    grad.addColorStop(1, 'rgba(255,255,255,0)');
    g.fillStyle = grad;
    g.fillRect(0, 0, s, s);
  });
}

export class Environment {
  constructor(scene) {
    this.scene = scene;
    const fog = new THREE.Color(FOG);
    scene.background = fog;
    scene.fog = new THREE.Fog(fog, 26, 115);

    // Luces: cielo frío, luna azulada y el farol cálido del aventurero.
    this.hemi = new THREE.HemisphereLight(0x9ab8cc, 0x241f18, 1.35);
    scene.add(this.hemi);
    this.zoneKey = -1;
    this.moon = new THREE.DirectionalLight(0xc8d6ff, 1.6);
    scene.add(this.moon, this.moon.target);
    this.lantern = new THREE.PointLight(0xffb36b, 14, 16, 1.4);
    scene.add(this.lantern);

    this.sky = this.#makeSky(fog);
    scene.add(this.sky);
    this.mist = this.#makeMist();
    for (const m of this.mist) scene.add(m);
    this.fireflies = this.#makeFireflies();
    scene.add(this.fireflies.points);

    const shadowTex = softDot(64);
    const shadowMat = new THREE.MeshBasicMaterial({ map: shadowTex, color: 0x000000, transparent: true, opacity: 0.55, depthWrite: false });
    const shadowGeom = new THREE.PlaneGeometry(1, 1).rotateX(-Math.PI / 2);
    this.playerShadow = new THREE.Mesh(shadowGeom, shadowMat);
    this.monsterShadow = new THREE.Mesh(shadowGeom, shadowMat.clone());
    this.monsterShadow.scale.set(2.2, 1, 2.4);
    scene.add(this.playerShadow, this.monsterShadow);
    this.lowDetail = false;
  }

  /** Calidad baja: sin luciérnagas ni bruma (lo decide Quality si el móvil no llega a 60 fps). */
  setLowDetail(low) {
    this.lowDetail = low;
    this.fireflies.points.visible = !low;
    for (const m of this.mist) m.visible = !low;
  }

  /** Colores de la zona según la distancia recorrida (solo se recalcula si cambian). */
  setDistance(distance) {
    const zone = Math.floor(distance / ZONE_LENGTH);
    const into = distance - zone * ZONE_LENGTH;
    const t = Math.max(0, (into - (ZONE_LENGTH - ZONE_FADE)) / ZONE_FADE);
    const key = zone * 1000 + Math.round(t * 50);
    if (key === this.zoneKey) return;
    this.zoneKey = key;
    const a = ZONES[zone % ZONES.length];
    const b = ZONES[(zone + 1) % ZONES.length];
    _zoneFog.copy(a.fog).lerp(b.fog, t);
    _zoneMid.copy(a.mid).lerp(b.mid, t);
    this.scene.fog.color.copy(_zoneFog);
    this.scene.background.copy(_zoneFog);
    this.hemi.color.copy(a.hemi).lerp(b.hemi, t);
    this.#paintSky(_zoneFog, _zoneMid);
  }

  update(dt, time, camera, player, chaser, onGround) {
    const p = player.position;
    this.setDistance(player.distance);
    this.sky.position.copy(camera.position);
    this.moon.position.copy(p).addScaledVector(MOON_DIR, 40);
    this.moon.target.position.copy(p);
    // El farol va delante y por encima del aventurero: ilumina el camino que viene.
    const yaw = player.visualYaw;
    this.lantern.position.set(p.x - Math.sin(yaw) * 1.6, p.y + 2.4, p.z - Math.cos(yaw) * 1.6);
    this.lantern.intensity = 14 * (0.93 + Math.sin(time * 9) * 0.04 + Math.sin(time * 23) * 0.03);

    // Bruma anclada al mundo (la textura se desplaza con el jugador) y que deriva despacio.
    for (const m of this.mist) {
      m.position.set(p.x, m.userData.y, p.z);
      const tex = m.material.map;
      tex.offset.set((p.x / MIST_SIZE) * tex.repeat.x + time * m.userData.drift, (-p.z / MIST_SIZE) * tex.repeat.y);
    }

    if (!this.lowDetail) this.#updateFireflies(dt, time, p);

    // Sombras de contacto: se encogen y aclaran con la altura; no se ven sobre los fosos.
    const ps = this.playerShadow;
    ps.visible = onGround.player;
    ps.position.set(p.x, 0.02, p.z);
    const lift = Math.max(0, Math.min(1, p.y / 2.5));
    ps.scale.setScalar(1.1 * (1 - lift * 0.5));
    ps.material.opacity = 0.55 * (1 - lift * 0.7);
    const ms = this.monsterShadow;
    ms.visible = chaser.mesh.visible && onGround.monster;
    ms.position.set(chaser.mesh.position.x, 0.025, chaser.mesh.position.z);
    ms.rotation.y = chaser.mesh.rotation.y;
  }

  #makeSky(fog) {
    const group = new THREE.Group();
    group.name = 'Sky';
    // Cúpula con degradado: casi negro arriba, color de la niebla en el horizonte.
    const dome = new THREE.SphereGeometry(190, 24, 12);
    dome.setAttribute('color', new THREE.Float32BufferAttribute(new Float32Array(dome.attributes.position.count * 3), 3));
    this.dome = dome;
    this.#paintSky(fog, ZONES[0].mid);
    const domeMesh = new THREE.Mesh(
      dome,
      new THREE.MeshBasicMaterial({ vertexColors: true, side: THREE.BackSide, fog: false, depthWrite: false }),
    );
    domeMesh.renderOrder = -2;

    // Estrellas en la mitad alta del cielo.
    const starPos = [];
    for (let i = 0; i < 500; i++) {
      const u = Math.random() * Math.PI * 2;
      const v = 0.25 + Math.random() * 0.75;
      const r = Math.sqrt(1 - v * v);
      starPos.push(Math.cos(u) * r * 180, v * 180, Math.sin(u) * r * 180);
    }
    const starGeom = new THREE.BufferGeometry();
    starGeom.setAttribute('position', new THREE.Float32BufferAttribute(starPos, 3));
    const stars = new THREE.Points(
      starGeom,
      new THREE.PointsMaterial({ color: 0xcfe0ff, size: 1.6, sizeAttenuation: false, fog: false, transparent: true, opacity: 0.75, depthWrite: false }),
    );
    stars.renderOrder = -1;

    // Luna con halo.
    const moonTex = canvasTexture(256, (g, s) => {
      const halo = g.createRadialGradient(s / 2, s / 2, s * 0.08, s / 2, s / 2, s / 2);
      halo.addColorStop(0, 'rgba(210,225,255,0.55)');
      halo.addColorStop(0.3, 'rgba(160,190,230,0.18)');
      halo.addColorStop(1, 'rgba(120,150,200,0)');
      g.fillStyle = halo;
      g.fillRect(0, 0, s, s);
      g.fillStyle = '#eef3ff';
      g.beginPath();
      g.arc(s / 2, s / 2, s * 0.1, 0, Math.PI * 2);
      g.fill();
      g.fillStyle = 'rgba(170,185,215,0.55)';
      for (const [x, y, r] of [[0.47, 0.47, 0.025], [0.54, 0.52, 0.018], [0.5, 0.56, 0.012]]) {
        g.beginPath();
        g.arc(s * x, s * y, s * r, 0, Math.PI * 2);
        g.fill();
      }
    });
    const moon = new THREE.Sprite(new THREE.SpriteMaterial({ map: moonTex, fog: false, depthWrite: false, transparent: true }));
    moon.scale.setScalar(90);
    moon.position.copy(MOON_DIR).multiplyScalar(170);
    moon.renderOrder = -1;

    group.add(domeMesh, stars, moon);
    return group;
  }

  /** Degradado de la cúpula: casi negro arriba, color de la niebla en el horizonte. */
  #paintSky(fog, mid) {
    const top = SKY_TOP;
    const pos = this.dome.attributes.position;
    const col = this.dome.attributes.color;
    const c = _skyColor;
    for (let i = 0; i < pos.count; i++) {
      const h = pos.getY(i) / 190;
      if (h < 0.08) c.copy(fog);
      else if (h < 0.35) c.copy(fog).lerp(mid, (h - 0.08) / 0.27);
      else c.copy(mid).lerp(top, Math.min(1, (h - 0.35) / 0.5));
      col.setXYZ(i, c.r, c.g, c.b);
    }
    col.needsUpdate = true;
  }

  #makeMist() {
    const tex = canvasTexture(256, (g, s) => {
      // Manchas suaves repetidas en los bordes para que la textura no tenga costuras.
      for (let i = 0; i < 46; i++) {
        const x = Math.random() * s;
        const y = Math.random() * s;
        const r = s * (0.08 + Math.random() * 0.18);
        for (const dx of [-s, 0, s]) {
          for (const dy of [-s, 0, s]) {
            const grad = g.createRadialGradient(x + dx, y + dy, 0, x + dx, y + dy, r);
            grad.addColorStop(0, 'rgba(255,255,255,0.22)');
            grad.addColorStop(1, 'rgba(255,255,255,0)');
            g.fillStyle = grad;
            g.fillRect(x + dx - r, y + dy - r, r * 2, r * 2);
          }
        }
      }
    });
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    return [
      { y: -4.5, color: 0x7f9ea6, opacity: 0.55, repeat: 5, drift: 0.004 },
      { y: -11, color: 0x5d7a80, opacity: 0.8, repeat: 3, drift: -0.003 },
    ].map(({ y, color, opacity, repeat, drift }) => {
      const map = tex.clone();
      map.repeat.set(repeat, repeat);
      const mesh = new THREE.Mesh(
        new THREE.PlaneGeometry(MIST_SIZE, MIST_SIZE).rotateX(-Math.PI / 2),
        new THREE.MeshBasicMaterial({ map, color, transparent: true, opacity, depthWrite: false }),
      );
      mesh.userData = { y, drift };
      mesh.name = 'Mist';
      return mesh;
    });
  }

  #makeFireflies() {
    const positions = new Float32Array(FIREFLIES * 3);
    const colors = new Float32Array(FIREFLIES * 3);
    const seeds = [];
    for (let i = 0; i < FIREFLIES; i++) {
      seeds.push({
        x: (Math.random() - 0.5) * FIREFLY_BOX * 2,
        y: Math.random() * 6 - 1,
        z: (Math.random() - 0.5) * FIREFLY_BOX * 2,
        phase: Math.random() * 100,
      });
    }
    const geom = new THREE.BufferGeometry();
    geom.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geom.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    const points = new THREE.Points(
      geom,
      new THREE.PointsMaterial({
        size: 0.35,
        map: softDot(),
        vertexColors: true,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
    );
    points.frustumCulled = false;
    points.name = 'Fireflies';
    return { points, seeds, positions, colors };
  }

  #updateFireflies(dt, time, center) {
    const { seeds, positions, colors, points } = this.fireflies;
    const box = FIREFLY_BOX;
    for (let i = 0; i < seeds.length; i++) {
      const f = seeds[i];
      // Coordenadas en un cubo que se repite alrededor del jugador: siempre hay luciérnagas cerca.
      const wrap = (v, c) => c + ((((v - c) % (2 * box)) + 3 * box) % (2 * box)) - box;
      const x = wrap(f.x + Math.sin(time * 0.3 + f.phase) * 2, center.x);
      const z = wrap(f.z + Math.cos(time * 0.25 + f.phase) * 2, center.z);
      positions[i * 3] = x;
      positions[i * 3 + 1] = f.y + Math.sin(time * 0.7 + f.phase * 2) * 0.6;
      positions[i * 3 + 2] = z;
      const glow = Math.max(0, Math.sin(time * 2 + f.phase)) ** 3;
      colors[i * 3] = 0.75 * glow;
      colors[i * 3 + 1] = 1.0 * glow;
      colors[i * 3 + 2] = 0.35 * glow;
    }
    points.geometry.attributes.position.needsUpdate = true;
    points.geometry.attributes.color.needsUpdate = true;
  }
}
